/**
 * After creating an invite: a personal QR code (generated locally, nothing leaves the browser),
 * a ready-made invitation text in the recipient's language, and copy/share/download buttons.
 * Works with WeChat (send the QR image, or scan it from the screen), Teams, WhatsApp or e-mail.
 */
import { useMemo, useState } from 'react';
import qrcode from 'qrcode-generator';
import { useT } from '../i18n.ts';
import { inviteText, type InviteLang } from '@tc/domain';

type Lang = InviteLang;
export { inviteText };

function qrPng(url: string, scale = 8): Promise<Blob> {
  const qr = qrcode(0, 'M');
  qr.addData(url);
  qr.make();
  const n = qr.getModuleCount();
  const margin = 4;
  const size = (n + margin * 2) * scale;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const g = canvas.getContext('2d')!;
  g.fillStyle = '#fff';
  g.fillRect(0, 0, size, size);
  g.fillStyle = '#000';
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) g.fillRect((c + margin) * scale, (r + margin) * scale, scale, scale);
  return new Promise((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error('qr'))), 'image/png'));
}

export function InviteShare({ url, hours, sender, org, defaultLang }: { url: string; hours: number; sender: string; org: string; defaultLang: Lang }) {
  const t = useT();
  const [lang, setLang] = useState<Lang>(defaultLang);
  const [note, setNote] = useState<string | null>(null);
  const text = inviteText(lang, { sender, org, url, hours });
  const qrSrc = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(url);
    qr.make();
    return qr.createDataURL(6, 24); // margin in pixels: 4 modules of quiet zone
  }, [url]);
  const flash = (msg: string) => {
    setNote(msg);
    setTimeout(() => setNote(null), 2500);
  };

  const copyText = async () => {
    await navigator.clipboard?.writeText(text);
    flash(t('copied'));
  };
  const copyImage = async () => {
    try {
      const png = await qrPng(url);
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
      flash(t('copied'));
    } catch {
      flash(t('copyImageFailed'));
    }
  };
  const download = async () => {
    const png = await qrPng(url);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(png);
    a.download = 'invite-qr.png';
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };
  const canShare = typeof navigator !== 'undefined' && !!navigator.share;
  const share = async () => {
    try {
      const file = new File([await qrPng(url)], 'invite-qr.png', { type: 'image/png' });
      const data: ShareData = navigator.canShare?.({ files: [file] }) ? { text, files: [file] } : { text };
      await navigator.share(data);
    } catch {
      /* cancelled */
    }
  };

  return (
    <div className="invite-share">
      <img className="qr" src={qrSrc} alt={t('inviteQr')} width={180} height={180} />
      <div className="invite-share-main">
        <label className="field">
          <span>{t('inviteLanguage')}</span>
          <select value={lang} onChange={(e) => setLang(e.target.value as Lang)}>
            <option value="zh-Hans">简体中文</option>
            <option value="sv-SE">Svenska</option>
            <option value="en">English</option>
          </select>
        </label>
        <textarea readOnly value={text} rows={5} onFocus={(e) => e.target.select()} aria-label={t('inviteText')} />
        <div className="row wrap">
          {canShare && <button type="button" className="btn small primary" onClick={() => void share()}>{t('share')}</button>}
          <button type="button" className="btn small" onClick={() => void copyText()}>{t('copyText')}</button>
          <button type="button" className="btn small" onClick={() => void copyImage()}>{t('copyQr')}</button>
          <button type="button" className="btn small" onClick={() => void download()}>{t('downloadQr')}</button>
          {note && <span className="muted small" role="status">{note}</span>}
        </div>
        <p className="muted small">{t('inviteShareHint')}</p>
      </div>
    </div>
  );
}
